import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "../../../design/alert";
import { Visit } from "@visitproof/types";
import moment from "moment";
import { VisitService } from "../../../services/VisitService";
import { Text } from "../../../design/text";

interface Props {
  visit?: Visit | null;
  onClose: () => void;
}

const UpdateVisitAlert: React.FC<Props> = ({ visit, onClose }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!visit || !visit.id) return;
    
    setIsUpdating(true);
    setError(null);
    
    try {
      await VisitService.updateVisit(visit.id, {
        startDate: visit.startDate,
        endDate: visit.endDate,
      });
      onClose();
    } catch (err) {
      console.error("Error updating visit:", err);
      setError("Failed to update visit. Please try again later.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  if (!visit) return null;

  const startTime = moment(visit.startDate.toDate()).format("LT");
  const endTime = moment(visit.endDate.toDate()).format("LT");
  const date = moment(visit.startDate.toDate()).format("MMM D, YYYY");

  return (
    <Alert open={visit != null} onClose={handleCancel}>
      <AlertTitle>Update Visit Schedule</AlertTitle>
      <AlertDescription>
        <div className="space-y-2">
          <Text>
            Are you sure you want to update this visit schedule?
          </Text>
          <div className="p-3 rounded-md bg-gray-50 dark:bg-gray-800">
            <Text className="font-medium">New Schedule:</Text>
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              {date} • {startTime} - {endTime}
            </Text>
          </div>
          {error && (
            <Text className="text-sm text-red-600 dark:text-red-400">
              {error}
            </Text>
          )}
        </div>
      </AlertDescription>
      <AlertActions>
        <Button 
          plain 
          onClick={handleCancel} 
          disabled={isUpdating}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={isUpdating}
          color="blue"
        >
          {isUpdating ? "Updating..." : "Update"}
        </Button>
      </AlertActions>
    </Alert>
  );
};

export default UpdateVisitAlert;
